/* ───────────────────────────────────────────────────────────────────────────
 * HOW A PLAN'S PRICE IS WRITTEN — one set of pure helpers for every surface
 *
 * No API client, no React: the signup picker and the billing page both render
 * prices with these, and a test of either can import them without dragging in
 * an axios instance. Nothing here computes a saving or a rate — every figure
 * is a field the server sent (src/billing/price-rules.js economics).
 * ─────────────────────────────────────────────────────────────────────────── */

export interface PricedPlan {
  price_cents: number;
  currency: string;
  billing_interval: string;
  contact_sales: boolean;
  effective_monthly_cents?: number | null;
  annual_savings_badge_percent?: number | null;
}

const symbolFor = (currency: string) =>
  currency?.toLowerCase() === 'usd' ? '$' : `${currency} `;

const amount = (cents: number) =>
  (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The price as billed: per unit per MONTH for a monthly row, per unit per YEAR
// for an annual one. An annual price is never shown as if it were monthly.
export const priceLabel = (p: PricedPlan) => {
  if (p.contact_sales) return 'custom';
  const per = p.billing_interval === 'year' ? 'yr' : 'mo';

  return `${symbolFor(p.currency)}${amount(p.price_cents)}/unit/${per}`;
};

// For an annual row only: what it works out to per month, labelled as such.
export const effectiveLabel = (p: PricedPlan) =>
  !p.contact_sales && p.billing_interval === 'year' && p.effective_monthly_cents
    ? `${symbolFor(p.currency)}${amount(p.effective_monthly_cents)}/unit/mo, billed annually`
    : null;

// The smallest saving across tiers: one badge, and it must be true for both.
export const annualBadgePercent = (plans: PricedPlan[]): number | null => {
  const values = plans
    .filter(p => p.billing_interval === 'year' && !p.contact_sales)
    .map(p => p.annual_savings_badge_percent)
    .filter((v): v is number => typeof v === 'number' && v > 0);

  return values.length ? Math.min(...values) : null;
};
